import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, Mail, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { QuantityStepper } from "../components/QuantityStepper";
import { ProductReviews } from "../components/ProductReviews";
import { Stars } from "../components/Stars";
import { useCart } from "../context/CartContext";
import { API, CONTACT, formatAUD } from "../lib/shop";

const VariantRow = ({ variant, product }) => {
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  const handleAdd = () => {
    if (qty < 1) return;
    addItem(
      {
        variant_id: variant.id,
        product_slug: product.slug,
        product_name: product.name,
        product_code: variant.product_code,
        description: variant.description,
        insulation_colour: variant.insulation_colour,
        units_per_pack: variant.units_per_pack,
        unit_price: variant.rate_per_pack,
      },
      qty
    );
    toast.success(`Added ${qty} × ${variant.product_code} to cart`);
    setQty(1);
  };

  return (
    <TableRow className="hover:bg-muted/60" data-testid={`variant-row-${variant.product_code.replace(/\s+/g, "-")}`}>
      <TableCell className="py-2 font-medium">{variant.description}</TableCell>
      <TableCell className="py-2 text-muted-foreground">{variant.insulation_colour || "—"}</TableCell>
      <TableCell className="py-2 font-mono text-xs">{variant.product_code}</TableCell>
      <TableCell className="py-2 text-right tabular-nums">{variant.units_per_pack ?? "—"}</TableCell>
      <TableCell className="py-2 text-right font-semibold tabular-nums text-primary">
        {variant.purchasable ? formatAUD(variant.rate_per_pack) : (
          <span className="text-xs font-medium text-muted-foreground">Price on request</span>
        )}
      </TableCell>
      <TableCell className="py-2">
        {variant.purchasable ? (
          <div className="flex items-center justify-end gap-2">
            <QuantityStepper value={qty} onChange={(v) => setQty(Math.max(1, v))} testId={`qty-${variant.product_code.replace(/\s+/g, "-")}`} />
            <button
              onClick={handleAdd}
              data-testid={`add-to-cart-${variant.product_code.replace(/\s+/g, "-")}`}
              className="flex items-center gap-1.5 rounded-sm bg-accent px-3 py-2 text-xs font-semibold text-accent-foreground transition-colors hover:bg-[#dfa557]"
            >
              <ShoppingCart className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <a
              href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(`Price enquiry: ${variant.product_code} — ${product.name}`)}`}
              data-testid={`enquire-${variant.product_code.replace(/\s+/g, "-")}`}
              className="flex items-center gap-1.5 rounded-sm border border-secondary px-3 py-2 text-xs font-semibold text-secondary transition-colors hover:bg-secondary hover:text-white"
            >
              <Mail className="h-3.5 w-3.5" /> Enquire
            </a>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20 text-center md:px-8">
        <p className="text-lg font-semibold">Product not found.</p>
        <Link to="/" className="mt-2 inline-block text-secondary underline">Back to catalogue</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <Skeleton className="h-8 w-64" />
        <div className="mt-8 grid gap-8 lg:grid-cols-[380px_1fr]">
          <Skeleton className="aspect-square rounded-sm" />
          <Skeleton className="h-96 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="product-detail-page">
      <Link to="/" data-testid="back-to-catalogue-link" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary">
        <ChevronLeft className="h-4 w-4" /> All products
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[380px_1fr]">
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="overflow-hidden rounded-sm border border-border bg-white">
            <img src={product.image} alt={product.name} className="aspect-square w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <Badge className="mt-5 rounded-sm bg-secondary text-white hover:bg-secondary">{product.category}</Badge>
          <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground lg:text-3xl" data-testid="product-title">
            {product.name}
          </h1>
          {product.rating?.count > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" data-testid="product-rating-summary">
              <Stars value={product.rating.average} />
              <span className="font-semibold text-foreground">{product.rating.average}</span>
              ({product.rating.count})
            </div>
          )}
          <p className="mt-3 text-sm text-muted-foreground">{product.blurb}</p>
          <p className="mt-4 text-xs text-muted-foreground">
            {product.variants.length} variants · Prices per pack, in AUD
          </p>
        </div>

        <div className="overflow-x-auto rounded-sm border border-border bg-white">
          <Table>
            <TableHeader className="sticky top-0 bg-muted">
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Colour</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="text-right">Units / Pack</TableHead>
                <TableHead className="text-right">Price / Pack</TableHead>
                <TableHead className="w-56 text-right">Order</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {product.variants.map((v) => (
                <VariantRow key={v.id} variant={v} product={product} />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ProductReviews slug={slug} />
    </div>
  );
}
